import { AxiosError, AxiosResponse } from 'axios';
import { del, edit, generateQueryString, get, post } from '@/utils';
import { ICreateProjectSendData } from '@/providers/create-project/types';
import { CITIZEN_API_URL } from '@/utils/axios/constants';
import request from '@/utils/axios/config';
import {
	ICheckProjectName,
	IProject,
	IProjectListItem,
	IProjectsListQuery,
	IProjectsListResponse
} from './types';

async function fetchProjects(
	getAll: boolean,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IProject[]> {
	try {
		const pageVariable = getAll ? 'all' : page + 1;
		const res = await get(`api/projects?page=${pageVariable}&items=${items}`);
		onResponse && onResponse(res.data?.pages, res?.data?.totalItems);
		const data = res.data as unknown as { projects: IProject[] };
		return data?.projects || [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să accesăm datele.');
	}
}

async function fetchQueryProjects(
	getAll: boolean,
	query: string,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IProject[]> {
	try {
		const pageVariable = getAll ? 'all' : page + 1;
		const res = await get(
			`api/projects${query}&page=${pageVariable}&items=${items}`
		);
		onResponse && onResponse(res.data?.pages, res?.data?.totalItems);
		const data = res.data as unknown as { projects: IProject[] };
		return data?.projects || [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să accesăm datele.');
	}
}

async function fetchSingleProject(projId: number | string): Promise<IProject> {
	try {
		const res = await get(`api/projects/${projId}`);
		const data = res.data as unknown as { project: IProject };
		return data?.project || {};
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să actualizăm proiectul.');
	}
}

async function postProject(
	projectObject: ICreateProjectSendData
): Promise<string | number> {
	try {
		const res = await post('api/projects', { ...projectObject });
		return res?.status === 201 ? 201 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să adăugăm proiectul.');
	}
}

async function postProjectName(
	params: ICheckProjectName,
		fsp?: number
): Promise<string | number> {
	try {
		const sendObject = params?.id ? params : { name: params.name };
		const res = await post('api/protected/projects/name/check', {
			data: { ...sendObject },
			fsp
		});
		return res?.status === 200 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să verificăm proiectul.');
	}
}

async function postProjectGeometry(params: any, fsp?: number): Promise<string | number> {
	try {
		const res = await post('api/projects/geometry/check', {
			geometry: params,
			fsp
		});
		return res?.status === 200 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să verificăm proiectul.');
	}
}

async function patchProject(
	projId: number | string,
	updatesObject: Omit<ICreateProjectSendData, 'elements'>
): Promise<string | number> {
	try {
		const res = await edit(`api/projects/${projId}`, { ...updatesObject });
		return res?.status === 200 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să actualizăm proiectul.');
	}
}

async function deleteProject(projId: number | string): Promise<string | number> {
	try {
		const res = await del(`api/projects/${projId}`);
		return res?.status === 200 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să ștergem proiectul.');
	}
}

async function fetchPosterQr(project: number): Promise<AxiosResponse<Blob>> {
	try {
		const res = await request.get(
			`${CITIZEN_API_URL}/api/protected/projects/${project}/poster`,
			{
				responseType: 'blob',
				withCredentials: true
			}
		);
		return res;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să accesăm posterul.');
	}
}

// GET api/projects (contract nou) — listă paginată cu filtre
// (Search / AdministrationId / StatusId / StageId / TypeId / PageNumber / PageSize / OrderBy / Ascending).
async function fetchProjectsList(
	query?: IProjectsListQuery,
	onResponse?: (totalPages: number, totalCount: number) => void
): Promise<IProjectListItem[]> {
	try {
		const queryString = generateQueryString({ ...query });
		const res = await get(`api/projects${queryString}`);
		const data = res.data as IProjectsListResponse;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return data.items || [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să accesăm proiectele.');
	}
}

export {
	fetchProjects,
	postProject,
	fetchSingleProject,
	deleteProject,
	patchProject,
	fetchPosterQr,
	fetchQueryProjects,
	postProjectName,
	postProjectGeometry,
	fetchProjectsList
};
